import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Toaster, toast } from 'sonner';
import { Eye, EyeOff, Loader2, Mail, CheckCircle, Upload, X, Check } from 'lucide-react';
import axios from 'axios';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const location = useLocation();
  const returnTo = location.state?.from || null;
  const isHostFlow = location.state?.intent === 'host';
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    bio: '',
    proofOfAddress: null,
    proofOfAddressUrl: '',
    idDocument: null,
    idDocumentUrl: '',
  });

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File must be under 10MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }
    setUploading(prev => ({ ...prev, [field]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      // Upload without auth token since user isn't registered yet - use a temp endpoint
      const res = await axios.post(`${API_URL}/api/upload/public`, fd);
      const urlField = field === 'proofOfAddress' ? 'proofOfAddressUrl' : 'idDocumentUrl';
      setFormData(prev => ({
        ...prev,
        [field]: file,
        [urlField]: res.data.url,
      }));
      toast.success(`${field === 'proofOfAddress' ? 'Proof of address' : 'ID document'} uploaded`);
    } catch {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const removeFile = (field) => {
    const urlField = field === 'proofOfAddress' ? 'proofOfAddressUrl' : 'idDocumentUrl';
    setFormData(prev => ({ ...prev, [field]: null, [urlField]: '' }));
  };

  const passwordChecks = useMemo(() => ({
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    digit: /\d/.test(formData.password),
    special: /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'/`~]/.test(formData.password),
  }), [formData.password]);

  const allPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;

  const canProceedStep1 = formData.name && formData.email && formData.phone && allPasswordValid && passwordsMatch;
  const canProceedStep2 = formData.address && formData.proofOfAddressUrl && formData.idDocumentUrl;
  const canSubmitGuest = canProceedStep1;

  const handleNextStep = () => {
    if (!allPasswordValid) {
      toast.error('Please meet all password requirements before continuing.');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords do not match.');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        address: formData.address,
        bio: formData.bio,
      });
      // Update profile with document URLs after registration
      // (we'll do this after email verification since we need auth)
      // Store doc URLs in localStorage temporarily
      if (formData.proofOfAddressUrl || formData.idDocumentUrl) {
        localStorage.setItem('pendingDocs', JSON.stringify({
          proof_of_address: formData.proofOfAddressUrl,
          id_document: formData.idDocumentUrl,
        }));
      }
      setRegisteredEmail(formData.email);
      setRegistrationComplete(true);
      // Persist return URL for after verification
      if (returnTo) {
        localStorage.setItem('returnAfterAuth', returnTo);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {
        email: registeredEmail
      });
      toast.success('Verification email sent! Please check your inbox.');
    } catch {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-[#F4EDE4] flex flex-col" data-testid="verification-pending-page">
        <Toaster />
        <header className="bg-white border-b py-4 px-6">
          <Logo size={34} />
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-[#FDF8F4] rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#D65A31]" />
              </div>
              <h1 className="text-2xl font-semibold mb-2" data-testid="verification-title">Check your email</h1>
              <p className="text-gray-500 mb-6">
                We've sent a verification link to<br />
                <span className="font-medium text-gray-900">{registeredEmail}</span>
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3 text-left">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium text-gray-900 mb-1">Next steps:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Open the email we sent you</li>
                      <li>Click the verification link</li>
                      <li>Start exploring MealHoster!</li>
                    </ol>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Didn't receive the email? Check your spam folder or
              </p>
              <Button
                onClick={handleResendVerification}
                disabled={resendLoading}
                variant="outline"
                className="w-full mb-4"
                data-testid="resend-btn"
              >
                {resendLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                ) : (
                  'Resend verification email'
                )}
              </Button>
              <Link to="/login" state={returnTo ? { from: { pathname: returnTo } } : undefined} className="text-[#D65A31] font-medium hover:underline text-sm" data-testid="back-to-login">
                Back to login
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EDE4] flex flex-col" data-testid="register-page">
      <Toaster />
      <header className="bg-white border-b py-4 px-6">
        <Logo size={34} />
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-2xl font-semibold text-center mb-2">Create your account</h1>
            <p className="text-gray-500 text-center mb-2">
              {isHostFlow
                ? 'Sign up to start hosting dining experiences'
                : 'Join MealHoster to discover amazing dining experiences'}
            </p>

            {/* Step indicator — host flow only */}
            {isHostFlow && (
              <div className="flex items-center justify-center gap-2 mb-8">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-[#D65A31] text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
                <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-[#D65A31]' : 'bg-gray-200'}`} />
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-[#D65A31] text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off">
              {/* Account details — shown on Step 1 (both flows) */}
              {(isHostFlow ? step === 1 : true) && (
                <div className="space-y-4" data-testid="register-step-1">
                  <p className="text-sm font-medium text-gray-600 mb-2">Account details</p>
                  <div>
                    <Label htmlFor="reg-name">Full Name *</Label>
                    <Input id="reg-name" name="reg-name" type="text" autoComplete="new-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" className="mt-1" required data-testid="name-input" />
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Email *</Label>
                    <Input id="reg-email" name="reg-email" type="email" autoComplete="new-email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="you@example.com" className="mt-1" required data-testid="email-input" />
                  </div>
                  <div>
                    <Label htmlFor="reg-phone">Mobile Phone *</Label>
                    <Input id="reg-phone" name="reg-phone" type="tel" autoComplete="new-phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 (555) 123-4567" className="mt-1" required data-testid="phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Password *</Label>
                    <div className="relative mt-1">
                      <Input id="reg-password" name="reg-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Min 8 chars, upper, lower, digit, special" required data-testid="password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {formData.password && (
                      <div className="mt-2 space-y-1" data-testid="password-requirements">
                        {[
                          { key: 'length', label: 'At least 8 characters' },
                          { key: 'uppercase', label: 'One uppercase letter' },
                          { key: 'lowercase', label: 'One lowercase letter' },
                          { key: 'digit', label: 'One number' },
                          { key: 'special', label: 'One special character (!@#$...)' },
                        ].map(({ key, label }) => (
                          <div key={key} className={`flex items-center gap-1.5 text-xs ${passwordChecks[key] ? 'text-green-600' : 'text-gray-400'}`} data-testid={`pw-req-${key}`}>
                            <Check className={`w-3.5 h-3.5 ${passwordChecks[key] ? 'opacity-100' : 'opacity-30'}`} />
                            {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="reg-confirmPassword">Confirm Password *</Label>
                    <Input id="reg-confirmPassword" name="reg-confirmPassword" type="password" autoComplete="new-password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} placeholder="Re-enter your password" className="mt-1" required data-testid="confirm-password-input" />
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1" data-testid="password-mismatch-error">Passwords do not match</p>
                    )}
                  </div>

                  {/* Guest flow: optional bio + submit directly */}
                  {!isHostFlow && (
                    <>
                      <div>
                        <Label htmlFor="bio">About You <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Textarea id="bio" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder="Tell us what kind of food experiences you enjoy..." className="mt-1" rows={3} maxLength={300} data-testid="bio-input" />
                        <p className="text-xs text-gray-400 mt-1 text-right">{formData.bio.length}/300</p>
                      </div>
                      <Button type="submit" disabled={loading || !canSubmitGuest} className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white py-6 rounded-lg font-semibold" data-testid="register-btn">
                        {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Creating...</> : 'Create account'}
                      </Button>
                    </>
                  )}

                  {/* Host flow: continue to Step 2 */}
                  {isHostFlow && (
                    <Button type="button" onClick={handleNextStep} disabled={!canProceedStep1} className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white py-6 rounded-lg font-semibold" data-testid="next-step-btn">
                      Continue
                    </Button>
                  )}
                </div>
              )}

              {/* Step 2 — host flow only */}
              {isHostFlow && step === 2 && (
                <div className="space-y-4" data-testid="register-step-2">
                  <p className="text-sm font-medium text-gray-600 mb-2">Verification & profile</p>
                  <div>
                    <Label htmlFor="address">Address *</Label>
                    <Input id="address" type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main St, City, Country" className="mt-1" required data-testid="address-input" />
                  </div>

                  {/* Proof of Address Upload */}
                  <div>
                    <Label>Proof of Address *</Label>
                    <p className="text-xs text-gray-500 mb-2">Utility bill, bank statement, or official letter</p>
                    {formData.proofOfAddress ? (
                      <div className="flex items-center gap-3 border rounded-lg p-3 bg-gray-50">
                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                        <span className="text-sm truncate flex-1">{formData.proofOfAddress.name}</span>
                        <button type="button" onClick={() => removeFile('proofOfAddress')} className="text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-[#D65A31] transition-colors" data-testid="proof-of-address-upload">
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFileUpload('proofOfAddress', e.target.files[0])} />
                        {uploading.proofOfAddress ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : <Upload className="w-5 h-5 text-gray-400" />}
                        <span className="text-sm text-gray-500">Upload image (JPG, PNG — max 10MB)</span>
                      </label>
                    )}
                  </div>

                  {/* ID Document Upload */}
                  <div>
                    <Label>ID Document *</Label>
                    <p className="text-xs text-gray-500 mb-2">Passport, driving licence, or national ID</p>
                    {formData.idDocument ? (
                      <div className="flex items-center gap-3 border rounded-lg p-3 bg-gray-50">
                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                        <span className="text-sm truncate flex-1">{formData.idDocument.name}</span>
                        <button type="button" onClick={() => removeFile('idDocument')} className="text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-[#D65A31] transition-colors" data-testid="id-document-upload">
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFileUpload('idDocument', e.target.files[0])} />
                        {uploading.idDocument ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : <Upload className="w-5 h-5 text-gray-400" />}
                        <span className="text-sm text-gray-500">Upload image (JPG, PNG — max 10MB)</span>
                      </label>
                    )}
                  </div>

                  {/* Bio */}
                  <div>
                    <Label htmlFor="bio">About You <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <Textarea id="bio" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder="Tell us about your culinary background..." className="mt-1" rows={3} maxLength={300} data-testid="bio-input" />
                    <p className="text-xs text-gray-400 mt-1 text-right">{formData.bio.length}/300</p>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 py-6" data-testid="back-step-btn">Back</Button>
                    <Button type="submit" disabled={loading || !canProceedStep2} className="flex-1 bg-[#D65A31] hover:bg-[#B84A28] text-white py-6 rounded-lg font-semibold" data-testid="register-btn">
                      {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Creating...</> : 'Create account'}
                    </Button>
                  </div>
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-500">
                Already have an account?{' '}
                <Link to="/login" state={returnTo ? { from: { pathname: returnTo } } : undefined} className="text-[#D65A31] font-medium hover:underline" data-testid="login-link">Log in</Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
